import { useApi } from '@src/app/context/Api';
import { isEmpty, isUndefined, uniq } from 'lodash';
import { useCallback, useMemo, useState } from 'react';

/**
 * Hook: useCommonCriteriaVersions
 *
 * Makes API requests to retrieve the assets required for the wizard experience.
 *
 * @returns {{isLoading: boolean, data: *}}
 */
export const useCommonCriteriaVersions = () => {
  const { queryClient, useApiQueries } = useApi('Criteria');

  const [theCriteria, setTheCriteria] = useState({});
  const [theQueries, setTheQueries] = useState([]);

  const queries = useApiQueries(theQueries);

  const isLoading = useMemo(() => {
    return (
      isEmpty(queries) ||
      Object.keys(queries).length !== theQueries.length ||
      !isEmpty(
        Object.keys(queries).filter((queryKey) => {
          const query = queries[queryKey];
          return query?.isLoading || query?.isFetching;
        })
      )
    );
  }, [queries, theQueries]);

  const fetchData = useCallback((criteria = {}) => {
    let versionIds = [];

    // Entrance Criteria
    if (
      !isUndefined(criteria?.entrance?.commonCriteriaVersionsIds) &&
      !isEmpty(criteria?.entrance?.commonCriteriaVersionsIds)
    ) {
      versionIds = [...versionIds, ...criteria.entrance.commonCriteriaVersionsIds];
    }

    // Exit Criteria
    if (
      !isUndefined(criteria?.exit?.commonCriteriaVersionsIds) &&
      !isEmpty(criteria?.exit?.commonCriteriaVersionsIds)
    ) {
      versionIds = [...versionIds, ...criteria.exit.commonCriteriaVersionsIds];
    }

    versionIds = uniq(versionIds);

    if (!isEmpty(versionIds)) {
      const requests = [];

      versionIds.forEach((versionId) => {
        requests.push({
          apiKey: 'Criteria',
          payload: { versionId },
          queryKey: `GetCommonCriteriaVersion-${versionId}`,
        });
      });

      if (requests !== theQueries) {
        setTheQueries(requests);
      }

      if (criteria !== theCriteria) {
        setTheCriteria(criteria);
      }
    } else {
      setTheCriteria({});
      setTheQueries([]);
      queryClient.removeQueries({
        predicate: (query) => {
          return query.queryKey?.[0].startsWith('GetCommonCriteriaVersion');
        },
      });
    }
  }, []);

  const versionsList = useMemo(() => {
    const theCommonCriteria = [];
    if (!isEmpty(queries)) {
      Object.keys(queries).forEach((queryKey) => {
        let context = null;

        const entrance = theCriteria?.entrance?.commonCriteriaVersionsIds?.find((versionId) => {
          return queries?.[queryKey]?.data?.id === versionId;
        });

        if (!isUndefined(entrance)) {
          context = 'entrance';
        }

        const exit = theCriteria?.exit?.commonCriteriaVersionsIds?.find((versionId) => {
          return queries?.[queryKey]?.data?.id === versionId;
        });

        if (!isUndefined(exit)) {
          context = 'exit';
        }

        theCommonCriteria.push({ ...queries?.[queryKey]?.data, ...{ context } });
      });
    }

    return theCommonCriteria;
  }, [queries]);

  return useMemo(() => {
    return { data: queries, fetch: fetchData, isLoading, versionsList };
  }, [isLoading, queries, versionsList]);
};
